<script>
import tourMixin from '@src/mixins/tour'
import pageMixin from '@src/mixins/pagination'
import { authComputed } from '@state/helpers'

import lazyLoadComponent from '@router/lazyload-component'

import Layout from '@layouts/main'
import { productStateMap, productTypeList } from '@utils/filter-value-to-text'
export default {
  page: {
    title: '所有項目',
    meta: [{ name: 'description', content: 'Product List' }],
  },

  components: {
    Layout,
    ListItem: () => lazyLoadComponent(import('./components/ListItem')),
    BaseButtonDialog: () => import('@components/BaseButtonDialog'),
    BaseButton: () => lazyLoadComponent(import('@components/BaseButton')),
  },
  mixins: [tourMixin, pageMixin],
  tourName: 'products',
  data() {
    return {
      addDialogVisible: false,
      dialogCalendarVisible: false,
      filterData: {
        state: '',
        activeOrders: '',
        hidden: '',
        order: 'desc',
        order_by: '',
      },
      stateList: Object.freeze(productStateMap),
      productTypeList: Object.freeze(productTypeList),
      orderByList: Object.freeze([
        {
          label: '依創建時間：近→遠',
          value: 'created_at',
        },
        {
          label: '依最後更新時間：近→遠',
          value: 'updated_at',
        },
      ]),
      productList: [],
      readiedComponents: [],
      modulesLimit: 0,
      modulesUsed: 0
    }
  },
  watch: {
    currentPage: {
      handler: 'tryToSearchProduct',
      immediate: true,
    },
    readiedComponents: {
      handler: 'componentsReady',
    },
  },
  mounted() {
    if (this.$route.params.showAddDialog === true) this.showAddDialog()
  },
  computed: {
    ...authComputed,
    merchantCalendarURL() {
      return `${window.location.origin}/api/${this.currentUser.merchantAlias}/${this.currentUser.merchantId}/product/ical`
    },
    showUsageWarning() {
      if(this.modulesLimit === 0) return false
      const percent25 = this.modulesLimit / 4
      const remnant = this.modulesLimit - this.modulesUsed
      return percent25 >= remnant
    }
  },
  methods: {
    handleToAddPage(type) {
      this.$router.push({ name: 'add-product', query: { type } })
    },
    async tryToSearchProduct() {
      const { items, modulesLimit, modulesUsed, total } = await this.$store.dispatch(
        'product/fetchList',
        Object.assign(this.filterData, this.pageQuery)
      )
      this.productList = items
      this.modulesUsed = modulesUsed
      this.modulesLimit = modulesLimit
      this.$nextTick(() => {
        this.$refs.pagination.updateTotal(total)
      })
    },
    updateReadiedComponents(id) {
      this.readiedComponents.push(id)
    },
    componentsReady() {
      if (this.readiedComponents.length === this.productList.length)
        this.showProductDialog()
    },
    showProductDialog() {
      if (this.$route.params.productId !== undefined)
        this.$refs[this.$route.params.productId][0].dialogVisible = true
    },
    showAddDialog() {
      this.addDialogVisible = true
    },
    copyCalendarUrl() {
      this.$refs.CalendarUrlInput.select()
      document.execCommand('copy')
      this.$message('連結複製成功')
    },
    openExternal(url) {
      window.open(url, '_blank')
    },
  },
}
</script>

<template>
  <Layout>
    <BasePageHeader>
      <template v-slot:title>所有項目</template>
      <!-- <template v-slot:right>
        <el-button @click="dialogCalendarVisible = true">
          <BaseIcon name="file-export" />
          <span>取得全店行事曆</span>
        </el-button>
      </template> -->
    </BasePageHeader>
    <el-alert
      v-if="showUsageWarning"
      :title="`您目前的項目使用數量： ${modulesUsed} / ${modulesLimit} 。提醒您，項目數量達到上限時，您無法在創建項目，欲擴增項目額度請聯絡業務處理。`"
      show-icon
      :closable="true"
      :class="$style.warning"
    ></el-alert>
    <BaseTableSearch
      v-slot="{ fields }"
      :default-value="filterData"
      @reset="tryToSearchProduct"
    >
      <el-form-item prop="state">
        <el-select
          v-model="fields.state"
          placeholder="選擇狀態"
          @change="tryToSearchProduct"
        >
          <el-option
            v-for="(state, stateValue) in stateList"
            :key="stateValue"
            :label="state.text"
            :value="stateValue"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item prop="activeOrders">
        <el-select
          v-model="fields.activeOrders"
          placeholder="是否有訂單"
          @change="tryToSearchProduct"
        >
          <el-option label="有訂單" value="1"></el-option>
          <el-option label="無訂單" value="0"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item prop="hidden">
        <el-select
          v-model="fields.hidden"
          placeholder="在商店首頁隱藏"
          @change="tryToSearchProduct"
        >
          <el-option label="顯示在商店首頁" value="0"></el-option>
          <el-option label="隱藏在商店首頁" value="1"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item prop="order_by">
        <el-select
          v-model="fields.order_by"
          placeholder="排序依據"
          @change="tryToSearchProduct"
        >
          <el-option
            v-for="order in orderByList"
            :key="order.value"
            :label="order.label"
            :value="order.value"
          ></el-option>
        </el-select>
      </el-form-item>
    </BaseTableSearch>
    <div
      :class="[
        $style['item__list'],
        $style[`item__list--last-${productList.length % 5}`],
      ]"
    >
      <ListItem
        :ref="product.id"
        v-for="product in productList"
        :key="product.id"
        :data="product"
        :class="$style.item"
        @updated="tryToSearchProduct"
        @ready="updateReadiedComponents"
      />
    </div>
    <BasePagination ref="pagination" :current-page.sync="currentPage" />
    <BaseButtonDialog
      v-can:api="'addProduct'"
      data-tour-text="點擊按鈕開始新增你的活動／課程至商店內。"
      title="請選擇新增的項目類型"
      tooltip="新增項目"
      :dialog-props="{ width: '400px', center: true }"
      :dialog-visible.sync="addDialogVisible"
      floating
    >
      <div :class="$style.types">
        <BaseButton
          v-for="(type, typeValue) in productTypeList"
          :key="typeValue"
          v-can:productType="typeValue"
          :icon="type.icon"
          :image="type.image"
          direction="vertical"
          :class="$style['types__item']"
          @click="handleToAddPage(typeValue)"
          >{{ type.name }}</BaseButton
        >
      </div>
    </BaseButtonDialog>
    <el-dialog
      title="全店行事曆"
      :visible.sync="dialogCalendarVisible"
      :append-to-body="true"
      top="0"
      width="40%"
    >
      <el-input
        ref="CalendarUrlInput"
        :value="merchantCalendarURL"
        :readonly="true"
      >
        <el-button slot="append" @click="copyCalendarUrl">複製此網址</el-button>
      </el-input>
      <p style="margin-top: 50px;">行事曆設定教學</p>
      <div style="margin-bottom: 15px;">
        <el-button
          style="width: 100%;"
          type="primary"
          plain
          @click="
            openExternal(
              'https://support.apple.com/zh-tw/guide/iphone/iph3d1110d4/ios'
            )
          "
          >設定於 Apple 行事曆</el-button
        >
      </div>
      <div>
        <el-button
          style="width: 100%;"
          type="primary"
          plain
          @click="
            openExternal(
              'https://support.google.com/calendar/answer/37100?co=GENIE.Platform%3DDesktop&hl=zh-Hant'
            )
          "
          >設定於 Google 行事曆</el-button
        >
      </div>
    </el-dialog>
  </Layout>
</template>
<style lang="scss" module>
@import '@design';

$column-number: 5;

.item {
  width: calc(100% / #{$column-number} - 15px);
  margin-bottom: 30px;

  &__list {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    &--last-2:after {
      width: calc(100% / #{$column-number} * 3 - 15px);
      content: '';
    }
    &--last-3:after {
      width: calc(100% / #{$column-number} * 2 - 15px);
      content: '';
    }
    &--last-4:after {
      width: calc(100% / #{$column-number} * 1 - 15px);
      content: '';
    }
  }
}

.types {
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  justify-content: space-between;

  &__item {
    min-width: 100px;
    margin-bottom: 20px;
  }
}

.warning {
  background-color: #BF7E45 !important;
  color: white !important;
  margin-top: -20px;
  margin-bottom: 20px;
  padding: 18px 16px;
  :global {
    .el-alert__closebtn {
      color: white !important;
      top: 18px;
      font-size: 18px;
    }
  }
}
</style>
